import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { compose } from 'recompose'

import {
  LeaderTraining,
  LeaderAwardUnit,
  LeaderHistory
} from 'containers/forms/leaders'
import { Form } from 'components/layout'
import { LeaderOverview } from 'components/sections/leaders'
import { PersonalBlock, AddressBlock } from 'components/sections/common'
import { FloatingBar, FormButton } from 'components/misc'
import { leaderValidate, mapSchema } from 'models/leaders'
import { form } from 'helpers/decorators'
import { apiConfiguration, apiContacts, apiLabels, apiParticipants } from 'helpers/decorators/api'
import DetailLoader from 'components/loaders/DetailLoader/DetailLoader'
import { asyncValidate } from 'models/persons/asyncValidate'
import { connect } from 'react-redux'
import { checkEmail } from '../../../../redux/modules/api/asyncValidations'
import { CONFIGURATION_KEYS, ROLES } from '../../../../constants/enums'
import { LabelsOverview } from '../../../../components/sections/contacts'
import { getActiveRole } from '../../../../utils/token'

const enhance = compose(
  apiParticipants(),
  apiConfiguration({
    getValues: [
      CONFIGURATION_KEYS.awardStaffRegistrationEmailAddress2.id,
      CONFIGURATION_KEYS.awardStaffContactDateOfBirth.id,
      CONFIGURATION_KEYS.awardStaffRegistrationPhone.id,
      CONFIGURATION_KEYS.awardStaffRegistrationMiddleName.id,
      CONFIGURATION_KEYS.awardStaffRegistrationGender.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressLine1.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressLine2.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressCity.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressZipCode.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressRegion.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressCountry.id
    ]
  }),
  apiLabels(),
  apiContacts(),
  connect(
    ({ api, auth }) => ({
      activeOrganization: auth.organization,
      labels: api.labels,
      availableLabels: api.labels.available.list,
      contactLabels: api.labels.contact.list
    }), { checkEmail }),
  form({
    form: 'leaderDetail',
    initialProps: 'leaders.detail',
    initialPropsMapper: mapSchema,
    validate: leaderValidate,
    asyncValidate
  })
)

class LeaderDetail extends Component {
  static propTypes = {
    leaders: PropTypes.object.isRequired,
    codeLists: PropTypes.object.isRequired,
    common: PropTypes.object.isRequired,
    change: PropTypes.func.isRequired,
    pristine: PropTypes.bool.isRequired,
    submitSucceeded: PropTypes.bool.isRequired,
    submitFailed: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    modal: PropTypes.func.isRequired,
    sendConfirmationEmail: PropTypes.func.isRequired,
    deleteLeader: PropTypes.func.isRequired,
    updateLeader: PropTypes.func.isRequired,
    sendRecoveryPassword: PropTypes.func.isRequired,
    moveLeader: PropTypes.func.isRequired,
    fetchCustomOrganizationContact: PropTypes.func,
    resetLeaderDetail: PropTypes.func,
    onFetchAvailableLabels: PropTypes.func.isRequired,
    onAssignContactLabel: PropTypes.func.isRequired,
    onUnassignContactLabel: PropTypes.func.isRequired,
    onGetPersonLabels: PropTypes.func.isRequired,
    labels: PropTypes.object.isRequired,
    availableLabels: PropTypes.array.isRequired,
    contactLabels: PropTypes.array.isRequired,
    configuration: PropTypes.object.isRequired,
    activeOrganization: PropTypes.object.isRequired,
    isOrgConfigurationTrue: PropTypes.func.isRequired
  }

  static defaultProps = {
    leaders: {}
  }

  componentDidMount() {
    if (!this.props.leaders.loaded) {
      return
    }

    const orgId = get(this.props, 'params.organizationId') || get(this.props.activeOrganization, 'id')

    const contactId = get(this.props.leaders, 'detail.id')

    orgId && contactId && this.props.fetchCustomOrganizationContact(orgId, contactId)
  }

  componentWillReceiveProps(nextProps) {
    if (get(this.props.leaders, 'detail.organization.id') !== get(nextProps, 'leaders.detail.organization.id')) {
      this.props.fetchCustomOrganizationContact(get(nextProps, 'leaders.detail.organization.id'), get(nextProps, 'leaders.detail.id'))
    }

    if (get(this.props, 'leaders.detail.id') !== get(nextProps, 'leaders.detail.id')) {
      const orgId = get(this.props, 'params.organizationId') || get(this.props.activeOrganization, 'id')

      const contactId = get(nextProps, 'leaders.detail.id')

      contactId && orgId && this.props.onFetchAvailableLabels(null, orgId, contactId)
      contactId && this.props.onGetContactLabels(contactId)
    }
  }

  render() {
    const {
      codeLists,
      common,
      leaders,
      change,
      pristine,
      submitSucceeded,
      submitFailed,
      invalid,
      handleSubmit,
      updateLeader,
      moveLeader,
      activeOrganization,
      configuration,
      labels,
      availableLabels,
      contactLabels,
      onFetchAvailableLabels,
      onAssignContactLabel,
      onUnassignContactLabel,
      onGetContactLabels
    } = this.props

    const isLeader = getActiveRole(activeOrganization) === ROLES.LEADER.u

    const loading = leaders.loading

    const organizationId = get(this.props, 'params.organizationId') || get(activeOrganization, 'id')

    const isContactLabelsEnabled = get(configuration, `valuesByOrganizationId[${organizationId}][${CONFIGURATION_KEYS.enableContactLabels.id}].booleanValue`)

    const showSecondaryEmail = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationEmailAddress2.id)

    const showPhone = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationPhone.id)

    const showMiddleName = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationMiddleName.id)

    const showDateOfBirth = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffContactDateOfBirth.id)

    const showAddressLine1 = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressLine1.id)

    const showAddressLine2 = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressLine2.id)

    const showCity = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressCity.id)

    const showZipCode = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressZipCode.id)

    const showRegion = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressRegion.id)

    const showCountry = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressCountry.id)

    const showAddressBlock = showAddressLine1 || showAddressLine2 || showCity || showZipCode || showRegion || showCountry


    return (
      <Form
        className="form"
        loading={loading}
        onSubmit={handleSubmit(updateLeader)}
        contentLoader={<DetailLoader modulesCount={3} />}
      >
        <div className="row">
          <div className="col-xs-6">
            <PersonalBlock
              titles={codeLists.titles.list}
              phoneTypes={codeLists.phoneTypes.list}
              change={change}
              data={leaders.detail}
              formPrefix="person."
              disableEmail
              personState={get(leaders, 'detail.person.personState')}
              migratedEmail={get(leaders, 'detail.person.migratedContactEmail')}
              decoPersonUpdate="leaderUpdate"
              canChangeEmail
              personId={get(leaders, 'detail.person.id')}
              omitNote={isLeader}
              isOrganizationContact
              showSecondaryEmail={showSecondaryEmail}
              showTertiaryEmail={showSecondaryEmail}
              showMiddleName={showMiddleName}
              showPhone={showPhone}
              showPhoneType
              showDateOfBirth={showDateOfBirth}
            />
          </div>

          <div className="col-xs-6">
            <div className="row">
              <div className="col-xs-12">
                <LeaderOverview
                  data={leaders.detail}
                  modal={this.props.modal}
                  sendConfirmationEmail={this.props.sendConfirmationEmail}
                  deleteLeader={this.props.deleteLeader}
                  codeLists={this.props.codeLists}
                  orgContactDeletionTypes={
                    codeLists.organizationContactReasonsOfDeletionTypes.list
                  }
                  sendRecoveryPassword={this.props.sendRecoveryPassword}
                  decoPersonUpdate="leaderUpdate"
                />
              </div>

              <div className="col-xs-12">
                <LeaderTraining
                  leaderId={get(leaders, 'detail.id')}
                  list={get(leaders, 'detail.person.awardLeaderTrainings')}
                />
              </div>

              <div className="col-xs-12">
                <LeaderHistory
                  data={leaders.detail}
                  leaderId={get(leaders, 'detail.id')}
                />
              </div>

              <div className="col-xs-12">
                <LeaderAwardUnit
                  moveLeader={moveLeader}
                  activeOrganization={activeOrganization}
                />
              </div>

              {isContactLabelsEnabled && !isLeader && <div className="col-xs-12">
                <LabelsOverview
                  data={contactLabels}
                  labels={labels}
                  availableLabels={availableLabels}
                  onFetchAvailableLabels={onFetchAvailableLabels}
                  onAssignContactLabel={onAssignContactLabel}
                  onUnassignContactLabel={onUnassignContactLabel}
                  onGetContactLabels={onGetContactLabels}
                  contactId={get(leaders, 'detail.id')}
                  organizationId={organizationId}
                />
              </div>}
            </div>
          </div>
        </div>

        {showAddressBlock && <div className="row">
          <div className="col-xs-6">
            <AddressBlock
              regions={common.regions.list}
              change={this.props.change}
              formPrefix="person.address"
              withCountry
              showAddressLine1={showAddressLine1}
              showAddressLine2={showAddressLine2}
              showCity={showCity}
              showZipCode={showZipCode}
              showRegion={showRegion}
              showCountry={showCountry}
            />
          </div>
        </div>}

        <FloatingBar>
          <FormButton
            pristine={pristine}
            submitSucceeded={submitSucceeded}
            submitFailed={submitFailed}
            invalid={invalid}
            type="submit"
            intlKey="button.save"
          >
            Save
          </FormButton>
        </FloatingBar>
      </Form>
    )
  }
}

export default enhance(LeaderDetail)
